import { useCallback, useEffect, useState } from 'react';
import api from '../utils/api';

export const CRM_FINANCIAL_YEAR_KEY = 'crm_selected_financial_year_id';

function applyYearSelection(years, setFinancialYearIdState) {
  const saved = localStorage.getItem(CRM_FINANCIAL_YEAR_KEY);
  const savedValid = saved && years.some((y) => String(y.id) === saved);
  if (savedValid) {
    setFinancialYearIdState(saved);
    return;
  }
  if (years.length > 0) {
    const current = years.find((y) => Number(y.is_current) === 1) || years[0];
    const id = String(current.id);
    setFinancialYearIdState(id);
    localStorage.setItem(CRM_FINANCIAL_YEAR_KEY, id);
    return;
  }
  setFinancialYearIdState('');
  localStorage.removeItem(CRM_FINANCIAL_YEAR_KEY);
}

export default function useCrmFinancialYear() {
  const [financialYears, setFinancialYears] = useState([]);
  const [financialYearId, setFinancialYearIdState] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchYears = useCallback(() => {
    setLoading(true);
    return api.get('/crm/financial-years')
      .then((res) => {
        const years = res.data?.data || [];
        setFinancialYears(years);
        applyYearSelection(years, setFinancialYearIdState);
      })
      .catch(() => {
        setFinancialYears([]);
        setFinancialYearIdState('');
        localStorage.removeItem(CRM_FINANCIAL_YEAR_KEY);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchYears().finally(() => {
      if (cancelled) return;
    });
    return () => { cancelled = true; };
  }, [fetchYears]);

  useEffect(() => {
    const onChanged = (event) => {
      if (event?.detail?.refresh) {
        fetchYears();
        return;
      }
      if (event?.detail?.financialYearId) {
        setFinancialYearIdState(String(event.detail.financialYearId));
      }
    };
    window.addEventListener('crmFinancialYearChanged', onChanged);
    return () => window.removeEventListener('crmFinancialYearChanged', onChanged);
  }, [fetchYears]);

  const setFinancialYearId = useCallback((value) => {
    setFinancialYearIdState(value);
    if (value) {
      localStorage.setItem(CRM_FINANCIAL_YEAR_KEY, value);
      window.dispatchEvent(new CustomEvent('crmFinancialYearChanged', { detail: { financialYearId: value } }));
    }
  }, []);

  const currentYear = financialYears.find((y) => String(y.id) === financialYearId) || null;

  return {
    financialYears,
    financialYearId,
    setFinancialYearId,
    loading,
    currentYear,
  };
}
